import { useNavigate } from "react-router-dom";
import { Button, IconButton, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Drawer, Box, Typography, Stack, Chip, Divider, LinearProgress } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AddIcon from "@mui/icons-material/Add";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import HistoryIcon from "@mui/icons-material/History";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";

import { useState } from "react";
import SearchBar from "../../components/SearchBar";
import CreateIncident from "../../Modals/createIncident";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const IncidentCenter = () => {
    const navigate = useNavigate();
    const [openModal, setOpenModal] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedIncident, setSelectedIncident] = useState<any>(null);
    const handleCloseModal = () => {
        setOpenModal(false);
    };
    const [searchTerm, setSearchTerm] = useState("");

    const rows = [
        {
            id: "INC-001",
            title: "Payment Gateway Timeout Errors",
            service: "Payment Service",
            severity: "Critical",
            status: "Investigating",
            owner: "Tech",
            assignee: "John Smith",
            impact: {
               gmv: "AED 120,000",
               orders: 340,
               vendors: 15
            },
            slaRemaining: "45 min",
            escalation: "L2",
            updated: "10 mins ago",
            aiSummary: "AI detected similar payment timeouts last week caused by gateway latency. Suggested action: reroute traffic or throttle retries.",
            rootCause: "External Gateway Latency spike detected in UAE region."
        },
        {
            id: "INC-002",
            title: "AI Recommendation Engine Degraded",
            service: "AI Engine",
            severity: "High",
            status: "Acknowledged",
            owner: "AI",
            assignee: "Sarah Chen",
            impact: {
               gmv: "AED 45,000",
               orders: 120,
               vendors: 4
            },
            slaRemaining: "1h 20m",
            escalation: "L1",
            updated: "30 mins ago",
            aiSummary: "Inference latency exceeded 200ms threshold. Linked to recent model deployment v2.4.1.",
            rootCause: "Model v2.4.1 resource exhaustion."
        },
        {
            id: "INC-003",
            title: "Database Connection Pool Exhaustion",
            service: "Database",
            severity: "High",
            status: "Open",
            owner: "Tech",
            assignee: "Unassigned",
            impact: {
               gmv: "AED 85,000",
               orders: 210,
               vendors: 8
            },
            slaRemaining: "15 min",
            escalation: "L1",
            updated: "1 hour ago",
            aiSummary: "Predicted escalation risk: High. Affected users spiking in central region.",
            rootCause: "Spike in write operations from bulk vendor import."
        },
    ];

    const predictiveAlerts = [
       { title: "Database connection pool nearing limit", confidence: "92%", time: "Predicted in 20 mins" },
       { title: "AI latency increasing beyond baseline", confidence: "85%", time: "Predicted in 45 mins" },
    ];

    return (
        <>
            <div className="main_title d_flex">
                <div className="left_s">

                    <h1>Incident Center</h1>
                </div>
                <div className="right_s">
                    <Button className="btn btn_grey" variant="outlined" startIcon={<HistoryIcon />}>Audit Log</Button>
                    <Button className="btn btn_grey" variant="outlined" >Export</Button>
                </div>
            </div>

            {/* AI Predictive Alerts */}
            <div className="predictive_alerts_banner mb_20">
               <div className="d_flex align-center gap_10">
                  <AutoFixHighIcon color="secondary" />
                  <h3>Potential Incidents (AI Predicted)</h3>
               </div>
               <div className="alert_grid">
                  {predictiveAlerts.map((alert, idx) => (
                     <div key={idx} className="predictive_alert_card">
                        <div className="status_dot pulse"></div>
                        <div className="info">
                           <b>{alert.title}</b>
                           <span>{alert.confidence} confidence • {alert.time}</span>
                        </div>
                     
                     </div>
                  ))}
               </div>
            </div>
            {/* ===== Quick Actions ===== */}

            {/* ======= Stats Overview ======= */}
            <div className="stats_boxes gap_m">

                {/* Active Incidents */}
                <div className="cards">
                    <div className="data_top">
                        <h2>
                            3
                            <p>Active Incidents</p>
                        </h2>

                        <figure className="danger">
                            <WarningAmberOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                    <div className="rating_box">
                        <p className="red">1 critical</p>
                    </div>
                </div>

                {/* Open */}
                <div className="cards">
                    <div className="data_top">
                        <h2>
                            1
                            <p>Open</p>
                        </h2>

                        <figure className="danger">
                            <ErrorOutlineOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                    <div className="rating_box">
                        <p className="gray">Awaiting assignment</p>
                    </div>
                </div>

                {/* Investigating */}
                <div className="cards">
                    <div className="data_top">
                        <h2>
                            1
                            <p>Investigating</p>
                        </h2>

                        <figure className="blue">
                            <TimelineOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                    <div className="rating_box">
                        <p className="blue">In progress</p>
                    </div>
                </div>

                {/* Resolved Today */}
                <div className="cards">
                    <div className="data_top">
                        <h2>
                            1
                            <p>Resolved Today</p>
                        </h2>

                        <figure className="success">
                            <CheckCircleOutlineIcon fontSize="large" />
                        </figure>
                    </div>
                    <div className="rating_box">
                        <p className="green">Last 24 hours</p>
                    </div>
                </div>

                {/* Avg Resolution */}
                <div className="cards">
                    <div className="data_top">
                        <h2>
                            2.4h
                            <p>Avg Resolution</p>
                        </h2>

                        <figure className="blue">
                            <AccessTimeOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                    <div className="rating_box">
                        <p className="green">-18% from last week</p>
                    </div>
                </div>

            </div>



            <div className="mt_20 incidents_layout cards">

                {/* LEFT: Incidents Table */}
                <div className="incidents_left">

                    {/* Filters */}
                    <div className="table_header">
                        <div className="left_s">
                            <SearchBar
                                searchTerm={searchTerm}
                               setDebouncedSearchTerm={() => { }}
                                placeholder="Search INC ID, Title, or Service..."
                            />
                            <div className="form">
                                <Select value="All" size="small" inputProps={{ "aria-label": "Impact Filter" }}>
                                    <MenuItem value="All">All Impact</MenuItem>
                                    <MenuItem value="High">High</MenuItem>
                                    <MenuItem value="Med">Medium</MenuItem>
                                </Select>
                            </div>
                        </div>
                        <div className="right_s">
                            <div className="form">
                                <Select value="All" size="small" fullWidth inputProps={{ "aria-label": "Severity Filter" }}>
                                    <MenuItem value="All">All Severities</MenuItem>
                                    <MenuItem value="Critical">Critical</MenuItem>
                                    <MenuItem value="High">High</MenuItem>
                                </Select>
                            </div>
                            <div className="form">
                                <Select value="All" size="small" fullWidth inputProps={{ "aria-label": "Status Filter" }}>
                                    <MenuItem value="All">All Status</MenuItem>
                                    <MenuItem value="Open">Open</MenuItem>
                                    <MenuItem value="Acknowledged">Acknowledged</MenuItem>
                                    <MenuItem value="Investigating">Investigating</MenuItem>
                                    <MenuItem value="Mitigated">Mitigated</MenuItem>
                                    <MenuItem value="Monitoring">Monitoring</MenuItem>
                                    <MenuItem value="Resolved">Resolved</MenuItem>
                                </Select>
                            </div>
                        </div>
                    </div>
                  
                    {/* Table */}
                    <TableContainer className="table_container">
                        <Table>

                            <TableHead>
                                <TableRow>
                                    <TableCell>ID / Title</TableCell>
                                    <TableCell>Business Impact (Risk)</TableCell>
                                    <TableCell>Responsibility</TableCell>
                                    <TableCell>SLA / Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow 
                                       hover 
                                       key={row.id} 
                                       onClick={() => {
                                          setSelectedIncident(row);
                                          setDrawerOpen(true);
                                       }}
                                       style={{ cursor: 'pointer' }}
                                    >
                                        {/* ID & Title */}
                                        <TableCell>
                                            <div className="brand_cell">
                                                <div className="brand_avatar">
                                                    {row.service.charAt(0)}
                                                </div>

                                                <div className="brand_info">
                                                    <b>{row.id}: {row.title}</b>
                                                    <span className="sub_text">{row.service}</span>
                                                </div>
                                            </div>
                                        </TableCell>

                                        {/* Business Impact */}
                                        <TableCell>
                                           <div className="impact_cell">
                                              <b>{row.impact.gmv}</b>
                                              <span className="sub_text">📦 {row.impact.orders} blocked • 🏪 {row.impact.vendors} vendors</span>
                                           </div>
                                        </TableCell>

                                        {/* Responsibility */}
                                        <TableCell>
                                           <div className="responsibility_cell">
                                              <Chip label={row.owner} size="small" className={`owner_chip ${row.owner.toLowerCase()}`} />
                                              <span className="sub_text">{row.assignee}</span>
                                           </div>
                                        </TableCell>

                                        {/* SLA & Status */}
                                        <TableCell>
                                           <div className="sla_status_cell">
                                              <span className={`status_chip ${row.status.toLowerCase()}`}>
                                                  {row.status}
                                              </span>
                                              <span className={`sla_timer ${row.slaRemaining.includes('min') && parseInt(row.slaRemaining) < 60 ? 'risk' : ''}`}>
                                                 ⏱ {row.slaRemaining}
                                              </span>
                                           </div>
                                        </TableCell>

                                        {/* Actions */}
                                        <TableCell align="right">
                                           <IconButton size="small"><MoreVertIcon /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </div>

            <div className="cards mt_20 incidents_right">

                {/* Service Status */}
                <div className="card">
                    <div className="card_header">
                        <h4>Service Status</h4>
                    </div>
                    <ul className="service_list">
                        <li><span>Payment Service</span><span className="dot degraded">Degraded</span></li>
                        <li><span>AI Engine</span><span className="dot degraded">Degraded</span></li>
                        <li><span>Database</span><span className="dot warning">Warning</span></li>
                        <li><span>CDN</span><span className="dot operational">Operational</span></li>
                        <li><span>API Gateway</span><span className="dot operational">Operational</span></li>
                        <li><span>Auth Service</span><span className="dot operational">Operational</span></li>
                    </ul>
                </div>
            </div>
            {/* Incident Detail Drawer */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: 500 } }}
            >
                {selectedIncident && (
                   <Box p={3} className="incident_drawer_content">
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                         <Typography variant="h5">Incident Detail: {selectedIncident.id}</Typography>
                         <IconButton onClick={() => setDrawerOpen(false)}><AddIcon sx={{ transform: 'rotate(45deg)' }} /></IconButton>
                      </Box>

                      <Chip label={selectedIncident.severity} color="error" className={`mb_20 severity_chip_large ${selectedIncident.severity.toLowerCase()}`} />

                      <Stack spacing={3}>
                         {/* AI Summary Section */}
                         <div className="ai_insight_card purple mb_20">
                            <div className="d_flex align-center gap_10 mb_10">
                               <AutoFixHighIcon color="secondary" />
                               <b>AI Incident Summary</b>
                            </div>
                            <Typography variant="body2">{selectedIncident.aiSummary}</Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Box>
                               <Typography variant="caption" color="text.secondary" display="block">Likely Root Cause:</Typography>
                               <Typography variant="body2" fontWeight={600}>{selectedIncident.rootCause}</Typography>
                            </Box>
                     
                         </div>

                         {/* Business Impact Breakdown */}
                         <div className="impact_breakdown_drawer">
                            <Typography variant="subtitle1" fontWeight={700} mb={1}>Business Impact</Typography>
                            <Box display="flex" gap={2} mb={2}>
                               <div className="metric_mini">
                                  <span>AED Risk</span>
                                  <b>{selectedIncident.impact.gmv}</b>
                               </div>
                               <div className="metric_mini">
                                  <span>Orders</span>
                                  <b>{selectedIncident.impact.orders}</b>
                               </div>
                               <div className="metric_mini">
                                  <span>Vendors</span>
                                  <b>{selectedIncident.impact.vendors}</b>
                               </div>
                            </Box>
                         </div>

                         {/* Timeline */}
                         <div className="incident_timeline">
                            <Typography variant="subtitle1" fontWeight={700} mb={2}>Timeline of Events</Typography>
                            <Stack spacing={2}>
                               <div className="timeline_item">
                                  <div className="time">10:45 AM</div>
                                  <div className="event">
                                     <b>AI Detected Latency Spike</b>
                                     <span className="sub">Automatic severity upgrade to 'Critical'</span>
                                  </div>
                               </div>
                               <div className="timeline_item">
                                  <div className="time">11:02 AM</div>
                                  <div className="event">
                                     <b>Acknowledged by {selectedIncident.assignee}</b>
                                     <span className="sub">Started rerouting traffic to secondary gateway</span>
                                  </div>
                               </div>
                            </Stack>
                         </div>

                         {/* Governance */}
                         <div className="governance_section">
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                               <Typography variant="subtitle1" fontWeight={700}>Governance</Typography>
                               <Chip label="PIR Required" variant="outlined" color="error" size="small" />
                            </Box>
                            <Box mt={1}>
                               <Typography variant="caption" color="text.secondary">Last modification by <b>{selectedIncident.assignee}</b> • {selectedIncident.updated}</Typography>
                            </Box>
                         </div>
                      </Stack>

                  
                   </Box>
                )}
            </Drawer>

            <CreateIncident
                open={openModal}
                onClose={handleCloseModal}
                setOpen={setOpenModal}
            />
        </>
    );
};

export default IncidentCenter;
